// Resumable small-jobs booking flow (web).
//
// The /order/:id review page collects typed state that lives only in memory (redux-form + local
// component state): the job description, the category questionnaire answers, the chosen booking flow
// and the appointment / schedule date. A refresh, a browser/gesture Back into the page, or a
// post-login return used to wipe all of it. This helper mirrors that state into sessionStorage —
// reusing the same `aiPrefill` sessionStorage convention already used by the AI assistant — keyed by
// the current order id, so the page can be rehydrated. The key is cleared once the booking is placed.
//
// ADDITIVE: this only persists/rehydrates client-typed state. It never touches the server, the order
// itself, or the payment calls.

const PREFIX = 'booking:';

function keyFor(orderId) {
    if (orderId == null || orderId === '') return null;
    return PREFIX + String(orderId);
}

// Read the saved draft for an order id. Returns {} when nothing is stored or on any error so callers
// can spread it safely.
export function readBookingDraft(orderId) {
    if (typeof window === 'undefined') return {};
    const key = keyFor(orderId);
    if (!key) return {};
    try {
        const raw = window.sessionStorage.getItem(key);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
        return {};
    }
}

// Merge-patch the saved draft. Only the provided keys are updated; `undefined` values are dropped so
// a partial save never clobbers fields it didn't touch. Recognised fields:
//   description   - the free-text "describe your job" value (OrderDetails.description)
//   questionnaire - the JSON string of questionnaire answers (OrderDetails.questionnaire)
//   scheduleDate  - the chosen schedule/appointment date (ms epoch or ISO string)
//   flow          - the chosen booking flow ('preferred' | 'choose')
export function writeBookingDraft(orderId, patch) {
    if (typeof window === 'undefined') return;
    const key = keyFor(orderId);
    if (!key || !patch || typeof patch !== 'object') return;
    try {
        const current = readBookingDraft(orderId);
        const next = { ...current };
        Object.keys(patch).forEach((k) => {
            if (patch[k] === undefined) return;
            next[k] = patch[k];
        });
        window.sessionStorage.setItem(key, JSON.stringify(next));
    } catch (e) {
        /* sessionStorage full / unavailable — non-fatal, the flow just isn't resumable */
    }
}

// --- Guest cart (deferred order creation) -----------------------------------------------------
//
// A logged-OUT visitor can browse /category and fill their cart entirely in memory (redux-form),
// exactly like the app. The server-side order/booking layer is user-scoped (getOrder/addOrderWhole
// key off request.user.id), so we do NOT create a guest Order row. Instead, when a guest clicks
// "Review Your Request" we stash the assembled cart here and send them through the existing
// login-with-return mechanism (?login=1&return=/order/replay). After login the replay path reads
// this cart back, creates the REAL order server-side (addOrderAction) and lands on /order/:id —
// where the now-authenticated user fills description/questionnaire/schedule/payment and Confirms.
//
// ADDITIVE: this only persists the client-side cart array. The auth wall on addOrderWhole /
// createUserBooking is untouched — a guest can FILL but never creates an order or reaches a charge.

const GUEST_CART_KEY = 'booking:guestCart';

// Persist the guest's in-progress cart (the `orderData` array from the BookingForm redux-form).
export function writeGuestCart(cart) {
    if (typeof window === 'undefined') return;
    if (!Array.isArray(cart) || cart.length === 0) return;
    try {
        window.sessionStorage.setItem(GUEST_CART_KEY, JSON.stringify(cart));
    } catch (e) {
        /* sessionStorage full / unavailable — non-fatal */
    }
}

// Read the saved guest cart (returns null when nothing is stored or on any error).
export function readGuestCart() {
    if (typeof window === 'undefined') return null;
    try {
        const raw = window.sessionStorage.getItem(GUEST_CART_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
    } catch (e) {
        return null;
    }
}

// Clear the saved guest cart (call once it has been replayed into a real order).
export function clearGuestCart() {
    if (typeof window === 'undefined') return;
    try {
        window.sessionStorage.removeItem(GUEST_CART_KEY);
    } catch (e) {
        /* ignore */
    }
}

// --- Guest booking meta (description / schedule / matching, carried across the login gate) ---------
//
// The logged-out /order-review screen also collects the job description, the schedule, the estimated
// hours and the matching choice (Choose-for-me vs a specific preferred pro). These are NOT part of the
// cart (they go to the booking mutation, not the Order row), so we stash them here on the final CTA and
// re-apply them on /order after login — robust to a hard reload / post-login return (unlike relying on
// in-memory redux surviving the auth navigation).
const GUEST_META_KEY = 'booking:guestMeta';

export function writeGuestMeta(meta) {
    if (typeof window === 'undefined') return;
    if (!meta || typeof meta !== 'object') return;
    try {
        window.sessionStorage.setItem(GUEST_META_KEY, JSON.stringify(meta));
    } catch (e) { /* non-fatal */ }
}

export function readGuestMeta() {
    if (typeof window === 'undefined') return null;
    try {
        const raw = window.sessionStorage.getItem(GUEST_META_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (e) { return null; }
}

export function clearGuestMeta() {
    if (typeof window === 'undefined') return;
    try {
        window.sessionStorage.removeItem(GUEST_META_KEY);
    } catch (e) { /* ignore */ }
}

// --- Guest job photos (uploaded logged-out, linked to the order after login) --------------------
//
// A logged-out visitor can upload job photos on /order-review: the file goes straight to the api
// upload endpoint (no auth needed there) which returns a fileName. We stash only those fileName
// strings (tiny) here; after the order is created post-login, each is LINKED to the order via
// uploadReviewImageMutation(orderId, fileName). So the physical upload happens as a guest; only the
// order association waits for the account.
const GUEST_PHOTOS_KEY = 'booking:guestPhotos';

export function readGuestPhotos() {
    if (typeof window === 'undefined') return [];
    try {
        const raw = window.sessionStorage.getItem(GUEST_PHOTOS_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
    } catch (e) { return []; }
}

export function writeGuestPhotos(list) {
    if (typeof window === 'undefined') return;
    try {
        const arr = Array.isArray(list) ? list.filter((x) => typeof x === 'string') : [];
        window.sessionStorage.setItem(GUEST_PHOTOS_KEY, JSON.stringify(arr));
    } catch (e) { /* non-fatal */ }
}

export function clearGuestPhotos() {
    if (typeof window === 'undefined') return;
    try {
        window.sessionStorage.removeItem(GUEST_PHOTOS_KEY);
    } catch (e) { /* ignore */ }
}

// Clear the saved draft for an order id (call on a successful booking).
export function clearBookingDraft(orderId) {
    if (typeof window === 'undefined') return;
    const key = keyFor(orderId);
    if (!key) return;
    try {
        window.sessionStorage.removeItem(key);
    } catch (e) {
        /* ignore */
    }
}
